import React, { Fragment } from 'react'
import styled from '@emotion/styled'
import Container, { NoPadContainer } from 'components/Container'
import { Bold, Slate } from 'components/Text'
import { Col, spacer } from 'stylist'
import { GrowCol } from 'components/Col'
import Tooltip, { InfoTooltip } from 'components/Tooltip'
import AddMoreButton from 'components/AddMoreButton'
import QualList from './QualList'
import { getQuals } from 'utils/api'
import QualDropdown from './QualDropdown'
import QualSettings from './QualSettings'
import concat from 'lodash/concat'
import without from 'lodash/without'
import omit from 'lodash/omit'
import size from 'lodash/size'
import pick from 'lodash/pick'

class Qualifications extends React.Component {
  /**
   * @namespace
   * @property {Number} selected Index displayed where -1 is nothing selected
   * @property {String[]} addedQualIDs [ID] of all quals to be displayed
   */
  state = {
    selected: size(this.props.questionsField.value) ? 0 : -1,
    addedQualIDs: size(this.props.questionsField.value)
      ? Object.keys(this.props.questionsField.value)
      : [],
    qualData: null
  }

  constructor(props) {
    super(props)
    if (this.props.locale) {
      this.updateQualData()
    }
  }

  componentDidUpdate(prevProps) {
    if (this.props.locale !== prevProps.locale) {
      this.updateQualData()
      this.clearQuals()
    }
  }

  updateQualData = () => {
    getQuals(this.props.locale).then(resp => {
      this.setState({ qualData: resp.data })
    })
  }

  /**
   * Erases all added qualifications
   */
  clearQuals = () => {
    const { questionsField, quotasField, zipFileField } = this.props
    questionsField.setValue({})
    quotasField.setValue({})
    zipFileField.setValue([])
    this.setState({ selected: -1, addedQualIDs: [] })
  }

  /**
   * Changes the currently displayed qualification
   */
  handleSelectQual = qualIndex => {
    this.setState({ selected: qualIndex })
  }

  /**
   * Initializes qualification with selected values
   * creating empty quotas for each selection
   * @param {String} qualID id of qual e.g. 'gender'
   * @param {Array} selected choices to be selected
   */
  initializeQual = (qualID, selected) => {
    const { questionsField, quotasField } = this.props
    const newQuotas = {}

    // Merge new qualification into form
    questionsField.setValue({
      ...questionsField.value,
      [qualID]: selected
    })

    selected.forEach((option, index) => {
      // Add empty quota for each selection
      // `option` and `index` will be the same unless
      // qualification options are not indexes (i.e. zip)
      newQuotas[`${qualID}_quota${index}`] = {
        limit: '',
        questions: {
          [qualID]: [option]
        }
      }
    })

    // Merge new quotas into form
    quotasField.setValue({
      ...quotasField.value,
      ...newQuotas
    })
  }

  /**
   * Adds a qualification to the form object and updates
   * the selected index to this one (which is now last)
   * @param {String} qualID id of qual e.g. 'gender'
   * @param {Array} selected choices to be selected initially
   */
  handleAddQual = qualID => {
    const { questionsField } = this.props

    this.initializeQual(qualID, [...this.state.qualData[qualID].options.keys()])

    this.setState({
      selected: size(questionsField.value),
      addedQualIDs: concat(this.state.addedQualIDs, qualID)
    })
  }

  /**
   * Removes the currently selected qualification from the form and state.
   */
  handleRemoveQual = () => {
    const { questionsField, quotasField, zipFileField } = this.props
    const selectedID = this.state.addedQualIDs[this.state.selected]
    const addedWithoutSelected = without(this.state.addedQualIDs, selectedID)

    questionsField.setValue(omit(questionsField.value, selectedID))
    quotasField.setValue(
      omit(
        quotasField.value,
        Object.keys(quotasField.value).filter(quota =>
          quota.startsWith(`${selectedID}_quota`)
        )
      )
    )
    if (selectedID === 'zip') {
      zipFileField.setValue([])
    }
    this.setState({ addedQualIDs: addedWithoutSelected })
    // Decrement selected if it would be out of bounds after removal
    if (addedWithoutSelected.length === this.state.selected) {
      this.setState({ selected: this.state.selected - 1 })
    }
  }

  render() {
    const selectedID = this.state.addedQualIDs[this.state.selected]
    const selectedQual = this.state.qualData && this.state.qualData[selectedID]
    const selectedQuals = pick(this.state.qualData, this.state.addedQualIDs)
    const unselectedQuals = omit(this.state.qualData, this.state.addedQualIDs)
    return (
      <Container className={this.props.className}>
        <GrowCol>
          <NoPadContainer className="heading">
            <GrowCol>
              <h5 className="left-heading">
                <Bold>
                  <Slate>Define your target audience</Slate>
                </Bold>
                <InfoTooltip
                  id="info-target-audience"
                  contents="Defining your target audience helps ensure that only the people meeting your desired criteria
                    will take your survey. Add the questions and qualifying answers you’ll use to screen for qualified
                    participants."
                />
              </h5>
            </GrowCol>
          </NoPadContainer>
          <NoPadContainer>
            <Col className="left-col">
              {this.state.qualData ? (
                <Fragment>
                  <QualList
                    quals={selectedQuals}
                    order={this.state.addedQualIDs}
                    onSelect={this.handleSelectQual}
                    selected={this.state.selected}
                  />
                  <QualDropdown
                    key={this.state.selected}
                    quals={unselectedQuals}
                    locale={this.props.locale}
                    onSelect={this.handleAddQual}
                  />
                </Fragment>
              ) : (
                <Tooltip contents="You must select a locale before adding qualifications.">
                  <AddMoreButton className="disabled-add-target" disabled>
                    Add Target
                  </AddMoreButton>
                </Tooltip>
              )}
            </Col>
            <GrowCol className="right-col">
              {selectedQual ? (
                <QualSettings
                  qualID={selectedID}
                  qualData={selectedQual}
                  onRemove={this.handleRemoveQual}
                  onInit={this.initializeQual}
                  locale={this.props.locale}
                  qualIDs={this.state.addedQualIDs}
                />
              ) : null}
            </GrowCol>
          </NoPadContainer>
        </GrowCol>
      </Container>
    )
  }
}

export default styled(Qualifications)`
  margin-top: ${spacer(5)};

  .left-heading {
    margin: auto 0;
  }

  .disabled-add-target {
    width: 120px;
  }

  .heading {
    margin-bottom: ${spacer(3)};
  }

  .left-col {
    margin-right: ${spacer(4)};
    width: 150px;
  }

  .right-col {
    justify-content: flex-start;
    margin-top: 0;
    margin-left: 0;
  }
`



// WEBPACK FOOTER //
// ./src/routes/estimate/subroutes/form/Qualifications.js